import type React from 'react';

import { readinessFieldDescriptions, type READINESSINFOKEYS } from '../helper';

interface Props {
	onToggle: (toggle: boolean) => void;
	value: boolean | undefined;
	field: READINESSINFOKEYS;
}

const FIELD_ICON: Record<READINESSINFOKEYS, string> = {
	hasSmartPhoneWithInternet: '📱',
	isAvailableAllDay: '🕐',
	hasPartyAgentExperience: '🎯',
	isNativeSpeaker: '🗣️',
	hasPowerBank: '🔋',
};

const SegmentedYesNo = ({ value, onToggle }: { value: boolean | undefined; onToggle: (v: boolean) => void }) => {
	const baseStyle: React.CSSProperties = {
		flex: 1,
		padding: '12px 18px',
		fontFamily: 'var(--font-family-semibold)',
		fontSize: '13px',
		letterSpacing: 'var(--tracking-wide)',
		textTransform: 'uppercase',
		cursor: 'pointer',
		border: 'none',
		background: 'transparent',
		transition: 'all 180ms ease',
		minHeight: '44px',
	};
	const selectedYes: React.CSSProperties = {
		backgroundColor: 'var(--color-brand-accent)',
		color: '#fff',
	};
	const selectedNo: React.CSSProperties = {
		backgroundColor: 'var(--color-text-secondary)',
		color: '#fff',
	};
	return (
		<div
			role='radiogroup'
			style={{
				display: 'flex',
				borderRadius: '999px',
				border: '1px solid var(--color-border-default)',
				overflow: 'hidden',
				backgroundColor: 'var(--color-bg-secondary)',
				width: 'fit-content',
			}}
		>
			<button
				type='button'
				role='radio'
				aria-checked={value === true}
				onClick={() => onToggle(true)}
				style={{ ...baseStyle, ...(value === true ? selectedYes : { color: 'var(--color-text-secondary)' }) }}
			>
				Yes
			</button>
			<button
				type='button'
				role='radio'
				aria-checked={value === false}
				onClick={() => onToggle(false)}
				style={{ ...baseStyle, ...(value === false ? selectedNo : { color: 'var(--color-text-secondary)' }) }}
			>
				No
			</button>
		</div>
	);
};

const FieldReadinessItem = ({ onToggle, value, field }: Props) => (
	<div
		style={{
			display: 'flex',
			flexDirection: 'column',
			gap: '14px',
			padding: '18px 20px',
			border: `1px solid ${
				value === true
					? 'var(--color-brand-accent)'
					: value === false
						? 'var(--color-border-default)'
						: 'var(--color-border-default)'
			}`,
			borderRadius: '14px',
			backgroundColor: 'var(--color-bg-elevated)',
			transition: 'border-color 200ms ease, box-shadow 200ms ease',
			boxShadow: value === true ? '0 0 0 4px rgba(68, 186, 181, 0.08)' : 'none',
		}}
	>
		<div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
			<span
				aria-hidden
				style={{
					fontSize: '22px',
					lineHeight: 1,
					marginTop: 2,
				}}
			>
				{FIELD_ICON[field]}
			</span>
			<div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
				<h5
					style={{
						margin: 0,
						fontFamily: 'var(--font-family-semibold)',
						fontSize: 'var(--text-body-size)',
						lineHeight: 'var(--leading-snug)',
						color: 'var(--color-text-primary)',
					}}
				>
					{readinessFieldDescriptions[field].title}
				</h5>
				<p
					style={{
						margin: 0,
						fontFamily: 'var(--font-family-regular)',
						fontSize: 'var(--text-body-sm-size)',
						lineHeight: 'var(--leading-normal)',
						color: 'var(--color-text-tertiary)',
					}}
				>
					{readinessFieldDescriptions[field].subtitle}
				</p>
			</div>
		</div>

		<SegmentedYesNo
			value={value}
			onToggle={onToggle}
		/>
	</div>
);

export default FieldReadinessItem;
